import React from "react";
import { Link } from "react-router-dom";
import ChevronRightIcon from "../icons/heroicons/solid/ChevronRight";
import missionGemGray from "../../assets/images/mission-gem-gray.png";
import missionGem from "../../assets/images/mission-gem.png";
import confetti from "../../assets/images/mission-confetti.png";

import missionCompletedTH from "../../assets/images/mission-completed-th.png";
import missionCompletedEN from "../../assets/images/mission-completed-en.png";
import bigcImg from "../../assets/images/big-c.jpg";
import missonTargeyGrey from "../../assets/images/misson-targey-grey.png";
import { useTranslation } from "react-i18next";
import { MaskInputHelper } from "../helpers/mask-input-helper";

const MissionsCard = ({
  item,
  missionsInCategory,
  imageUrls,
  isExpiredMission,
  templateDetail,
  actionLink,
  showDescription,
}) => {
  const { t, i18n } = useTranslation();
  const seniorLayout = localStorage.getItem("seniorLayout") === "true";

  const calculateMilestones = (missions) => {
    const milestones = [];
    const numberOfMilestones = missions.length;

    missions.forEach((mission, index) => {
      milestones.push({
        positionPercent: ((index + 1) / numberOfMilestones) * 100,
        completeValue: mission.completeValue,
      });
    });

    return { milestones };
  };

  const calculateRealProgressPercent = (missions, progressValue) => {
    let totalCompleteValue = missions[missions.length - 1]?.completeValue || 0;

    if (progressValue >= totalCompleteValue) {
      return 100;
    }

    let prevCompleteValue = 0;
    for (let i = 0; i < missions.length; i++) {
      const currentCompleteValue = missions[i].completeValue;
      if (progressValue <= currentCompleteValue) {
        const sectionProgress =
          (progressValue - prevCompleteValue) /
          (currentCompleteValue - prevCompleteValue);
        const basePercent = (i / missions.length) * 100;
        const sectionWidth = (1 / missions.length) * 100;
        return basePercent + sectionProgress * sectionWidth;
      }
      prevCompleteValue = currentCompleteValue;
    }
    return 100;
  };

  const { milestones } = calculateMilestones(missionsInCategory);
  const progressPercent = calculateRealProgressPercent(
    missionsInCategory,
    item.progressValue || 0
  );

  const progressUnit = (unit) => {
    return unit === "F"
      ? t("times")
      : unit === "S"
      ? t("bahts")
      : unit === "P"
      ? t("points")
      : "";
  };

  return (
    <Link
      to={actionLink}
      className="w-full block rounded-2xl overflow-hidden my-[10px]"
    >
      <div
        className={`w-full rounded-2xl overflow-hidden border border-overlay-12 ${
          isExpiredMission ? "opacity-40" : ""
        }`}
      >
        <div className="relative w-full h-full flex p-2 bg-white">
          <div className="w-full flex flex-col justify-between gap-y-4 pl-3 pt-3 pr-6">
            {/* ข้อมูลภารกิจ */}
            <div className="w-full flex gap-x-3">
              <div className="mt-1 w-16 h-auto">
                <div className="w-full rounded-full overflow-hidden">
                  <img
                    src={
                      imageUrls[item.id] ? imageUrls[item.id] : missonTargeyGrey
                    }
                    alt="Mission Thumbnail"
                    className="w-full h-full object-cover"
                    onError={(e) => (e.target.src = bigcImg)}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-y-1 w-full pt-1 text-left">
                <div
                  className={`font-semibold line-clamp-2 ${
                    seniorLayout ? "text-2xl" : "text-xl"
                  }`}
                >
                  {item.name}
                </div>
                <div
                  className={`font-normal line-clamp-2 ${
                    seniorLayout ? "text-lg" : "text-sm"
                  }`}
                >
                  {item.shortDescription?.[0]}
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.targeted && (
                    <div className="h-[1.125rem] px-3 rounded-full flex justify-center items-center bg-secondary-background-red">
                      <span
                        className={`font-medium text-secondary-text-invert ${
                          seniorLayout ? "text-sm" : "text-xs"
                        }`}
                      >
                        {t("mission_tag_targeted")}
                      </span>
                    </div>
                  )}
                </div>
                {item.isCompleted && (
                  <div className="absolute w-12 h-12 -right-2 -translate-x-1/2 top-10 z-10">
                    <img
                      src={
                        i18n.language === "en"
                          ? missionCompletedEN
                          : missionCompletedTH
                      }
                      className="w-12 h-12 object-cover"
                      alt="Claimed"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Progress Bar + Milestones */}
            <div className="relative w-full h-2 bg-[#d3d3d3] rounded-full mt-4">
              <div
                className="absolute left-0 top-0 h-full bg-[#ffa500] rounded-full"
                style={{ width: `${Math.min(progressPercent, 100)}%` }}
              ></div>

              {milestones.map((milestone, index) => {
                const isLast = index === milestones.length - 1;
                return (
                  <div
                    key={index}
                    className={`absolute top-1/2 transform -translate-y-1/2 ${
                      isLast ? "right-0 translate-x-1/2" : "-translate-x-1/2"
                    }`}
                    style={
                      isLast ? {} : { left: `${milestone.positionPercent}%` }
                    }
                  >
                    <img
                      src={
                        (item.progressValue || 0) >= milestone.completeValue
                          ? missionGem
                          : missionGemGray
                      }
                      alt="Milestone"
                      className="w-[35px] h-[35px] object-contain"
                    />
                  </div>
                );
              })}
            </div>

            {/* Progress Numbers */}
            <div
              className={`w-full flex justify-between font-normal mt-2 ${
                seniorLayout ? "text-lg" : "text-xs"
              }`}
            >
              <div>
                {!item.isCompleted ? (
                  <span className="font-medium whitespace-nowrap">
                    <span className="text-green-600">
                      {MaskInputHelper.formatNumber(item.progressValue)}
                    </span>
                    /{MaskInputHelper.formatNumber(item.completeValue)}
                    <span className="ml-1">{progressUnit(item.unit)}</span>
                  </span>
                ) : item.employeeMission === "1" ? (
                  <span className="font-normal whitespace-nowrap">
                    <span className="text-red-600">
                      {t("mission_usedFullEmployeeMission")}
                    </span>
                  </span>
                ) : (
                  <span className="font-normal whitespace-nowrap">
                    <span class="float-left mr-2">
                      <img
                        src={confetti}
                        alt="Claimed"
                        className="w-4 h-4 object-cover"
                      />
                    </span>
                    <span className="text-green-600">{t("claimed")}</span>
                  </span>
                )}
              </div>
              <div
                className={`${
                  item.dayToExpire < 4
                    ? "text-red-500"
                    : "text-secondary-text-note"
                }`}
              >
                {item.dayToExpire < 0
                  ? t("expired")
                  : item.dayToExpire === 0
                  ? t("mission_toDayExpired")
                  : t("mission_card_expiry", { days: item.dayToExpire })}
              </div>
            </div>
          </div>

          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center">
            {templateDetail === false ? (
              <ChevronRightIcon className="w-6 h-6 text-primary" />
            ) : (
              ""
            )}
          </div>
        </div>
        {showDescription == true ? (
          <div className="relative w-full h-full flex p-2">
            <div className="w-full flex flex-col justify-between gap-y-4 pl-3 pt-3 pr-6">
              <div
                className={`font-normal ${
                  seniorLayout ? "text-lg" : "text-sm"
                }`}
              >
                {item.description}
              </div>
            </div>
          </div>
        ) : (
          ""
        )}
      </div>
    </Link>
  );
};

export default MissionsCard;
